/**
 * ColorManager: tracks per-field color palettes and notifies listeners on change.
 *
 * A "palette" here is any object that maps identifiers to color strings
 * (e.g. { Ecoli: '#3182bd', Kpneumo: '#e6550d' }).  Consumers decide the
 * internal shape; ColorManager treats it as an opaque value.
 */

/** A mapping from category/identifier values to CSS color strings. */
export type ColorPalette = Record<string, string>;

/** Callback invoked when a field's palette changes or is removed. */
export type PaletteChangeCallback = (palette: ColorPalette | null, source: string) => void;

export class ColorManager {
  private readonly fieldPalettes = new Map<string, ColorPalette>();
  private readonly listeners = new Map<string, PaletteChangeCallback[]>();
  updateInProgress = false;

  setPalette(field: string, palette: ColorPalette, source = 'unknown'): void {
    if (this.updateInProgress) return;

    const previousPalette = this.fieldPalettes.get(field);
    if (previousPalette === palette) return;

    this.fieldPalettes.set(field, palette);
    this.notifyListeners(field, palette, source);
  }

  getPalette(field: string): ColorPalette | undefined {
    return this.fieldPalettes.get(field);
  }

  hasPalette(field: string): boolean {
    return this.fieldPalettes.has(field);
  }

  removePalette(field: string): void {
    this.fieldPalettes.delete(field);
    this.notifyListeners(field, null, 'removed');
  }

  addListener(field: string, callback: PaletteChangeCallback): void {
    if (!this.listeners.has(field)) {
      this.listeners.set(field, []);
    }
    this.listeners.get(field)!.push(callback);
  }

  removeListener(field: string, callback: PaletteChangeCallback): void {
    const callbacks = this.listeners.get(field);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  private notifyListeners(field: string, palette: ColorPalette | null, source: string): void {
    this.updateInProgress = true;

    const callbacks = this.listeners.get(field);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(palette, source);
        } catch (error) {
          console.error('Error in ColorManager listener:', error);
        }
      });
    }

    setTimeout(() => {
      this.updateInProgress = false;
    }, 0);
  }

  clearAll(): void {
    this.fieldPalettes.clear();
    this.listeners.clear();
  }
}

export const colorManager = new ColorManager();
